import { useEffect, useRef, useState } from "react";
import { gamesApi } from "@/lib/api";
import ArcadeShell from "@/components/games/ArcadeShell";

const CELL = 18;
const COLS = 18;
const ROWS = 18;

function SnakeBoard({ onGameOver, speed }) {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const dirRef = useRef({ x: 1, y: 0 });
  const nextDirRef = useRef({ x: 1, y: 0 });
  const stateRef = useRef({ snake: [{ x: 8, y: 9 }], food: { x: 12, y: 9 }, over: false });
  const scoreRef = useRef(0);
  const interval = { slow: 170, normal: 130, fast: 95 }[speed] || 130;

  const setDir = (x, y) => {
    const d = dirRef.current;
    if (d.x === -x && d.y === -y) return; // no reverse
    nextDirRef.current = { x, y };
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowUp", "w"].includes(e.key)) setDir(0, -1);
      else if (["ArrowDown", "s"].includes(e.key)) setDir(0, 1);
      else if (["ArrowLeft", "a"].includes(e.key)) setDir(-1, 0);
      else if (["ArrowRight", "d"].includes(e.key)) setDir(1, 0);
      if (e.key.startsWith("Arrow")) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const tick = () => {
      const st = stateRef.current;
      if (st.over) return;
      dirRef.current = nextDirRef.current;
      const head = { x: st.snake[0].x + dirRef.current.x, y: st.snake[0].y + dirRef.current.y };
      if (head.x < 0 || head.y < 0 || head.x >= COLS || head.y >= ROWS || st.snake.some((s) => s.x === head.x && s.y === head.y)) {
        st.over = true;
        onGameOver(scoreRef.current);
        return;
      }
      st.snake.unshift(head);
      if (head.x === st.food.x && head.y === st.food.y) {
        scoreRef.current += 1;
        setScore(scoreRef.current);
        do {
          st.food = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) };
        } while (st.snake.some((s) => s.x === st.food.x && s.y === st.food.y));
      } else {
        st.snake.pop();
      }
      // draw
      ctx.fillStyle = "#F4F1EA";
      ctx.fillRect(0, 0, COLS * CELL, ROWS * CELL);
      ctx.fillStyle = "#FF3B30";
      ctx.fillRect(st.food.x * CELL + 2, st.food.y * CELL + 2, CELL - 4, CELL - 4);
      st.snake.forEach((s, i) => {
        ctx.fillStyle = i === 0 ? "#2E5C45" : "#438263";
        ctx.fillRect(s.x * CELL + 1, s.y * CELL + 1, CELL - 2, CELL - 2);
      });
    };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [onGameOver, interval]);

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="font-display text-lg font-bold">Score: {score}</p>
      <canvas ref={canvasRef} width={COLS * CELL} height={ROWS * CELL} className="rounded-xl border border-border" data-testid="snake-canvas" />
      <div className="grid grid-cols-3 gap-2" style={{ width: 160 }}>
        <span />
        <button onClick={() => setDir(0, -1)} className="rounded-lg bg-secondary py-3 font-bold">▲</button>
        <span />
        <button onClick={() => setDir(-1, 0)} className="rounded-lg bg-secondary py-3 font-bold">◀</button>
        <button onClick={() => setDir(0, 1)} className="rounded-lg bg-secondary py-3 font-bold">▼</button>
        <button onClick={() => setDir(1, 0)} className="rounded-lg bg-secondary py-3 font-bold">▶</button>
      </div>
      <p className="text-xs text-muted-foreground">Use arrow keys / WASD or the buttons.</p>
    </div>
  );
}

export default function SnakeGame() {
  return (
    <ArcadeShell title="Snake Dash" game="snake" stateKey="snakeState" stateFn={gamesApi.snakeState} unlockFn={gamesApi.snakeUnlock} scoreFn={gamesApi.snakeScore}>
      {({ onGameOver, speed }) => <SnakeBoard onGameOver={onGameOver} speed={speed} />}
    </ArcadeShell>
  );
}
